import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

import type { FeatureKind } from '../features/feature-badges/constants';
import { usePageCrumbs } from '../layout/header-crumbs';
import { visuallyHidden } from '../lib/visually-hidden';
import type { Crumb } from '../navigation/nav-items';
import { cssVar } from '../theme/tokens';
import { FeatureBadge } from './ui/FeatureBadge';

export const APP_TITLE = 'Lions Admin';

export interface PageHeaderProps {
  /** The page's only h1, also the document title. */
  title: string;
  /** One line under the title saying what the page is for. */
  description?: ReactNode;
  /** Buttons on the right (Create, filters); they wrap under the title on narrow screens. */
  actions?: ReactNode;
  /** Crumbs after the sidebar trail, for pages deeper than a sidebar item or tab (a team's detail). */
  breadcrumbs?: readonly Crumb[];
  /** Feature badge beside the title (outside the h1): a static concept page or a new one. */
  badge?: FeatureKind;
  /** A short line above the title (the dashboard's date). */
  eyebrow?: ReactNode;
  /**
   * Shown in the title's place (the dashboard greeting); the h1 keeps `title` for screen readers
   * and the document title, visually hidden.
   */
  displayTitle?: string;
  /**
   * The page draws its own header (Match Centre's scoreboard): only the visually hidden h1 and
   * the document title are rendered, and the page shows its badge itself.
   */
  titleHidden?: boolean;
}

/**
 * The page title (the page's only h1, also the document title) in the v3 style, a description and
 * the actions. The breadcrumb trail lives in the header; `breadcrumbs` adds deeper crumbs to it.
 */
export function PageHeader({
  title,
  description,
  actions,
  breadcrumbs,
  badge,
  eyebrow,
  displayTitle,
  titleHidden = false,
}: PageHeaderProps) {
  usePageCrumbs(breadcrumbs);
  if (titleHidden) {
    return (
      <>
        <title>{`${title} · ${APP_TITLE}`}</title>
        <h1 style={visuallyHidden}>{title}</h1>
      </>
    );
  }
  return (
    <>
      <title>{`${title} · ${APP_TITLE}`}</title>
      <Stack
        direction="row"
        sx={{
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 4,
          mb: 5,
          minHeight: cssVar('control-h-md'),
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {eyebrow ? (
            <Typography
              variant="bodySm"
              component="p"
              sx={{ mb: 1, fontWeight: 600, color: cssVar('text-subtle') }}
            >
              {eyebrow}
            </Typography>
          ) : null}
          <Stack direction="row" sx={{ alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography
              variant="pageTitle"
              component="h1"
              // A style, not sx: sx reads the 1 px sizes as 100%.
              style={displayTitle ? visuallyHidden : undefined}
              sx={{ overflowWrap: 'anywhere' }}
            >
              {title}
            </Typography>
            {displayTitle ? (
              <Typography
                variant="pageTitle"
                component="p"
                aria-hidden
                sx={{ overflowWrap: 'anywhere' }}
              >
                {displayTitle}
              </Typography>
            ) : null}
            {badge ? <FeatureBadge kind={badge} /> : null}
          </Stack>
          {description ? (
            <Typography sx={{ mt: 1.5, color: cssVar('text-muted') }}>{description}</Typography>
          ) : null}
        </Box>
        {actions ? (
          <Stack direction="row" sx={{ flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            {actions}
          </Stack>
        ) : null}
      </Stack>
    </>
  );
}
